import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  useCreateItemMutation,
  useUploadFileMutation,
  useSetTagsForEntityMutation,
} from '../../../services/api';
import AutocompleteInput from '../../autocomplete-input';
import { useNotificationsProvider } from '../../../context/NotificationsContext';
import ImagePicker from '../../image-picker';
import { TagsMultiSelect } from '../../tags-multi-select';

AddItemDialog.propTypes = {
  title: PropTypes.string,
  parent: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onCreated: PropTypes.func,
};

export default function AddItemDialog({ title, parent, open, onClose, onCreated }) {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const [createItem, { isLoading: isCreating }] = useCreateItemMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const [setTags, { isLoading: isSettingTags }] = useSetTagsForEntityMutation();
  const [images, setImages] = useState([]);

  const loading = isCreating || isUploading || isSettingTags;

  const schema = z.object({
    itemTitle: z.string().trim().min(1, t('items.errors.title-required', 'Title is required')),
    itemDescription: z.string().trim().min(1, t('items.errors.description-required', 'Description is required')),
    itemParent: z.any().optional(),
    selectedTags: z.array(z.any()),
  });

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      itemTitle: '',
      itemDescription: '',
      itemParent: parent ?? {},
      selectedTags: [],
    },
  });

  const resetState = () => {
    form.reset({
      itemTitle: '',
      itemDescription: '',
      itemParent: parent ?? {},
      selectedTags: [],
    });
    setImages([]);
  };

  const onOpenChange = (next) => {
    if (!next) {
      if (loading) return;
      resetState();
      onClose?.();
    }
  };

  const handleAddError = (err, prefix) => {
    const errorCode = err?.data?.validationErrors?.[0]?.errorCode;
    if (errorCode === 'ITEM_DETAILS_ALREADY_USED') {
      showNotification('error', t(`${prefix}.already-exists-error`));
    } else if (errorCode === 'ITEM_LEVEL_EXCEEDED') {
      showNotification('error', t(`${prefix}.level-exceeded-error`));
    } else {
      showNotification('error', t('common.api-error'));
    }
  };

  const onSubmit = async (values) => {
    const data = {
      title: values.itemTitle.trim(),
      description: values.itemDescription.trim(),
    };
    if (values.itemParent?.id) data.parentId = values.itemParent.id;

    try {
      if (images.length > 0) {
        const uploaded = await Promise.all(
          images.map((image) =>
            uploadFile({ file: image, meta: { contentType: image.type } }).unwrap(),
          ),
        );
        data.photosJson = JSON.stringify(uploaded.map((r) => r.url));
      }
      const created = await createItem(data).unwrap();
      if (values.selectedTags.length > 0 && created?.id) {
        await setTags({
          taggableType: 'Item',
          taggableId: created.id,
          tagIds: values.selectedTags.map((tag) => tag.id),
        }).unwrap();
      }
      resetState();
      onCreated?.();
    } catch (err) {
      handleAddError(
        err,
        images.length > 0 ? 'items.add-items-dialog' : 'items.add-item-dialog',
      );
    }
  };

  // Keep the parent in sync with the prop (used when the page passes a
  // pre-selected parent from the tree's "add child" affordance).
  useEffect(() => {
    form.setValue('itemParent', parent ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => { if (loading) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (loading) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{title ?? t('items.add-item-dialog.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('items.add-item-dialog.title')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-5"
            noValidate
          >
            <ImagePicker onChange={setImages} />

            <FormField
              control={form.control}
              name="itemTitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('items.add-item-dialog.item-title')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="itemDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('items.add-item-dialog.description-title')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="itemParent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('items.add-item-dialog.parent-item-title')}</FormLabel>
                  <AutocompleteInput
                    onChange={(val) => field.onChange(val)}
                    defaultValue={field.value}
                    value={field.value}
                    resource="items/autocomplete"
                    canDelete
                    refreshOnInputChange
                    clearOnBlur={false}
                    freeSolo
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="selectedTags"
              render={({ field }) => (
                <FormItem>
                  <TagsMultiSelect
                    value={field.value}
                    onChange={field.onChange}
                    label={t('items.add-item-dialog.tags-title')}
                    placeholder={t('tags.multi-select.placeholder')}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
